import type { ComponentType } from 'react';
import type { FeatureId, ScreenId, SharedComponentName } from '@whinerp/contracts/generated/spec';
import type { AlwaysOn, EntitlementKey } from '@whinerp/contracts/permissions';

/**
 * How a person gets to a screen.
 *
 * AR-20: an API operation with no way in is not a delivered feature. Screens
 * therefore declare their entrance, and check:features walks the graph from
 * the navigation roots. A screen whose only claimed entrance is another
 * unreachable screen is unreachable, which is exactly the case a hand-written
 * "is it reachable?" list gets wrong.
 */
export type ReachedBy =
  /** Appears in the nav rail or on Home's index of areas. */
  | { kind: 'nav'; item: string }
  /** Linked from another screen. `how` names the affordance a person clicks. */
  | { kind: 'screen'; from: ScreenId; how: string }
  /** Reached from Setup home's category cards. */
  | { kind: 'setup'; section: string }
  /** Opened by the command palette. */
  | { kind: 'command'; action: string }
  /** A public URL: no session, workspace resolved from the token or slug. */
  | { kind: 'public' }
  /** The authentication screens, reached when there is no session at all. */
  | { kind: 'unauthenticated' };

export type ScreenSurface = 'page' | 'modal' | 'slide-over' | 'docked-panel';

/**
 * Whether the screen sits inside the application shell.
 *
 * Most do. A few are deliberately full-bleed with no navigation - signup, sign
 * in, the setup wizard, the public surfaces - and 04_Screens says so for each.
 * Declaring it means the shell owns the page's one `main` landmark and a
 * screen inside it never renders a second, which is the accessibility failure
 * this replaces.
 */
export type ScreenChrome = 'shell' | 'bare';

/**
 * A state a screen implements, or a stated reason it does not apply.
 *
 * 04_Screens marks some states "n/a" - a sign-in form has no empty state
 * because it is never a list of nothing. Saying so explicitly is the point:
 * a missing state and a state that does not apply are different, and only one
 * of them is a gap.
 */
export type ScreenState<P> = ComponentType<P> | { notApplicable: string };

export interface ScreenStateComponents<P> {
  /** Teaches the next action. Never marketing copy. */
  empty: ScreenState<P & { reason: 'no-records' | 'filtered-to-nothing' | 'permission-denied' }>;
  /** Skeleton matching the final layout. */
  loading: ScreenState<P>;
  /** Inline; names what failed and what to do. */
  error: ScreenState<P & { error: unknown; retry: () => void }>;
}

export function isImplemented<P>(state: ScreenState<P>): state is ComponentType<P> {
  return typeof state === 'function';
}

export interface ScreenDefinition<P = Record<string, never>> {
  id: ScreenId;
  /** react-router path. Matches the Route column of 04_Screens. */
  route: string;
  surface: ScreenSurface;
  /** Defaults to 'shell'. */
  chrome?: ScreenChrome;
  title: string;
  /** Features this screen surfaces. check:features reads this. */
  features: FeatureId[];
  entitlement: EntitlementKey | AlwaysOn;
  reachedBy: ReachedBy[];
  /**
   * Shared components from 06_Component_Library this screen renders.
   * check:screens renders the screen and asserts each one appears.
   */
  components: SharedComponentName[];
  /**
   * Screen-specific parts, by their data-component name.
   *
   * The library covers what is shared; a screen's own pieces - a signup form,
   * a provisioning progress list - are still things 04_Screens names and still
   * things that can quietly stop rendering. Declaring them means the check
   * examines the whole screen rather than only its reusable half.
   */
  elements?: string[];
  /**
   * Components that only exist after somebody acts.
   *
   * An impact preview appears when a switch is touched; a completion popover
   * when a step is completed. The render check renders a screen and cannot see
   * these, so declaring them here moves the burden rather than dropping it:
   * `check:screens` requires each one to have been seen by a UI test. That is
   * the stronger demand of the two - "it renders" versus "somebody's test
   * drove the product to the point where it renders".
   */
  afterInteraction?: string[];
  states: ScreenStateComponents<P>;
  component: ComponentType<P>;
  /** Route params the screen needs, with values the checks can render with. */
  sampleParams?: Record<string, string>;
  /**
   * For a record screen: which object to take a real record of.
   *
   * A hardcoded sample id would be a fixture that has to be kept in step with
   * the seed, and the failure would be a screen rendering its not-found state
   * while the check reported it as rendered. The harness resolves this against
   * whatever the workspace actually holds.
   */
  sampleRecord?: SampleRecord | SampleRecord[];
  /**
   * For a public surface: a real link, issued for the checks.
   *
   * Without it a portal screen renders its refusal page for every check that
   * walks the registry - so the accessibility pass, the responsive pass and
   * the render pass would all audit the same "this link does not work" page
   * and count three screens as covered. The one state those screens are
   * actually used in would be audited nowhere.
   */
  samplePortalToken?: { surface: 'booking' | 'preferences' | 'portal' | 'partner'; param: string };
}

// The registry holds screens with different prop types; only the screen's own
// module knows which. `never` here would make every registry entry unusable,
// so this is the one place the erasure is deliberate.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type AnyScreenDefinition = ScreenDefinition<any>;

/**
 * A route parameter filled from a real record.
 *
 * An array when a route names more than one - `/deals/:id/quotes/:qid`. The
 * resolvers all take the oldest record of their object, and the seed puts its
 * one quote on the oldest deal, so the pair that comes back belongs together
 * rather than being two unrelated records that happen to typecheck.
 */
export interface SampleRecord {
  /** A registered object, resolved through the object registry. */
  objectKey?: string;
  /**
   * A table that is not a registered object, resolved by taking its oldest row.
   *
   * Some screens edit real records that carry no permission surface of their
   * own - a price book, a layout. Those have no object definition and never
   * will, and the alternative to this was a literal id in `sampleParams`: the
   * pricing editor carried `{ id: 'default' }`, which is not a uuid and
   * matched nothing, so the screen rendered its error state and every check
   * counted it as rendered (P-7).
   */
  table?: { name: string; idColumn: string };
  param: string;
}

export function defineScreen<P>(definition: ScreenDefinition<P>): ScreenDefinition<P> {
  if (!definition.reachedBy.length) {
    throw new Error(
      `Screen ${definition.id} declares no way in. Every screen must be reachable (AR-20); ` +
        `if it is genuinely only reachable from another screen, say so with { kind: 'screen', from, how }.`,
    );
  }
  if (!definition.features.length) {
    throw new Error(`Screen ${definition.id} declares no features. A screen exists to surface something.`);
  }
  if (definition.components.length === 0 && (definition.elements ?? []).length === 0) {
    throw new Error(
      `Screen ${definition.id} declares nothing that must render. Name the shared components it uses, ` +
        `or its own parts in 'elements' - otherwise check:screens can only prove it did not throw.`,
    );
  }
  for (const [state, value] of Object.entries(definition.states)) {
    if (typeof value === 'object' && !(value as { notApplicable?: string }).notApplicable?.trim()) {
      throw new Error(
        `Screen ${definition.id} marks its ${state} state as not applicable without saying why.`,
      );
    }
  }
  return definition;
}
